Ext.define('SA.data.AttributesCache', {
    extend: 'SA.data.EntityCache',
    singleton: true,

    extractResultsFromQueryResponse: ({results}) => results,
    idField: 'attribute',

    constructor() {
        this.callParent();

        this.queuePurge = SA.CoalescingMicroTask.coalesce(() => this.purgeUnreferenced());
    },

    add(attribute) {
        Ext.Array.from(attribute).forEach(this.superclass.add, this);
    },

    await(ids) {
        return new Promise(resolve => this.getPromise(ids).then(() => resolve()));
    },

    buildQuery(ids) {
        return {
            fields: this.getFields(),
            type: 'attributes',
            where: ['in', 'attribute', ids]
        };
    },

    canResolveGeographicUnit({attributes}) {
        return SA.data.GeographicUnits.getAllowedIdsForAttributes(attributes).length > 0;
    },

    formatValue(value, format) {
        if (value == null) {
            return 'N/A';
        }

        return SA.formatValue(value, format);
    },

    get(ids, fn, scope) {
        var single = !Array.isArray(ids),
            idsArray = this.getIds(ids),
            userAttributeIds = SA.data.UserAttributes.getIds(),
            systemAttributeIds = Ext.Array.difference(idsArray, userAttributeIds),
            systemAttributes = this.callParent([systemAttributeIds, fn, scope]),
            entries = idsArray.map(getEntryForId);

        return single ? entries[0] : entries;

        function getEntryForId(id) {
            if (userAttributeIds.includes(id)) {
                return SA.data.UserAttributes.get(id);
            }

            return systemAttributes.shift();
        }
    },

    getAttributeFormat(attribute) {
        var {attributeFormat} = this.get(attribute) || {};

        return attributeFormat || null;
    },

    getBadgedLabelHtml(attribute) {
        return SA.BadgedAttributeLabel.getHtml({attribute: this.get(attribute)});
    },

    getDatasetName(id) {
        return SA.data.Datasets.getName(this.get(id).dataset_series);
    },

    getFields() {
        return [
            'attribute', 'name', 'year', 'geographicUnits', 'formatString', 'increments', 'geographicRelease',
            'type', 'group', 'country', 'censusRelease', 'projected', 'dataset_series', 'default_position', 'attribute_class',
            'attributeFormat', 'vendor', 'indent', 'shortName', 'crosstabAvailableLocations'
        ];
    },

    getFormat(attribute) {
        return attribute ? this.get(attribute).formatString : null;
    },

    getFormatter(attributeId) {
        var formatString = this.getFormat(attributeId);

        return {format: value => this.formatValue(value, formatString)};
    },

    getGeographicReleasesForAttributes(attributes) {
        var releases = this.get(attributes).map(attribute => attribute.geographicRelease);

        return Ext.Array.unique(releases);
    },

    getId(attribute) {
        return Ext.isObject(attribute) ? attribute.attribute : attribute;
    },

    getIds(attributes) {
        return Ext.Array.from(attributes).map(attribute => this.getId(attribute));
    },

    getLabel(attribute) {
        var attribute = SA.data.AttributesCache.get(attribute),
            {name, year} = attribute || {};

        return attribute ? `${name}, ${year}` : null;
    },

    getModel(attribute) {
        if (Array.isArray(attribute)) {
            return attribute.map(attribute => this.getModel(attribute));
        }

        var data = this.get(attribute),
            roundingFactor = 1 / data.increments,
            roundValue = value => Math.round(value * roundingFactor) / roundingFactor;

        return new SA.model.Attribute({
            id: data.attribute,
            label: `${data.name}, ${data.year}`,
            datasetSeries: data.dataset_series,
            name: data.name,
            censusReleaseYear: data.censusRelease,
            type: data.type,
            indent: data.indent,
            valueFormatter: value => SA.formatValue(roundValue(value), data.formatString),
            hasPolygonClass: SA.data.AttributeClasses.isPolygonClass(data.attribute_class)
        });
    },

    async getModelPromise(id) {
        var item = await SA.data.AttributesCache.getPromise(id);

        return SA.data.AttributesCache.getModel(item);
    },

    getTickFormatter(values, attribute) {
        var {attributeFormat, formatString} = this.get(attribute),
            formatter = SA.Number.getFormatterWithConsistentDecimalsPerSuffix(values);

        if (attributeFormat === 'percent') {
            return value => this.formatValue(value, formatString);
        }

        return value => {
            var prefix = attributeFormat === 'currency' ? '$' : '';

            return prefix + formatter(value);
        };
    },

    hasCensusRelease(attribute, censusRelease) {
        return this.get(attribute)?.censusRelease === censusRelease;
    },

    hasDefaultVerticalPosition(id) {
        return this.get(id).default_position === 'vertical';
    },

    isCrosstabAttributeAvailableInLocation(attribute, location) {
        var locations = this.get(attribute).crosstabAvailableLocations;

        return !locations || locations.includes(location);
    },

    isCurrency(attribute) {
        return this.getAttributeFormat(attribute) === 'currency';
    },

    isEstimate(id) {
        return this.isProjection(id) && this.get(id).year <= SA.System.getYear() + 1;
    },

    isPercent(attribute) {
        return this.getAttributeFormat(attribute) === 'percent';
    },

    isPolygonAttribute(id) {
        return SA.data.AttributeClasses.isPolygonClass(this.get(id).attribute_class);
    },

    isProjection(id) {
        return this.get(id).projected === 'true';
    },

    isScarboroughCrosstabAttribute(id) {
        var series = this.get(id).dataset_series;

        return SA.data.Vendors.getDatasetModel(series).isScarboroughCrosstab();
    },

    isSimmonsCrosstabAttribute(id) {
        var series = this.get(id).dataset_series;

        return SA.data.Vendors.getDatasetModel(series).isSimmonsCrosstab();
    },

    isUserAttribute(attribute) {
        var id = this.getId(attribute);

        return SA.data.UserAttributes.isUserAttribute(id);
    },

    purgeUnreferenced() {
        var all = Object.keys(this.cache),
            referenced = SA.Array.unique([
                SA.data.Projects.getAllReferencedAttributes(),
                SA.data.NewProjectAttributes.getAllIds(),
                SA.data.RecentAttributes.getIds(),
                SA.data.FavoriteAttributes.getIds(),
                SA.Sidebar.getDataBrowseOrSearchWindowResultAttributeIds()
            ]);

        all.filter(id => !referenced.includes(id)).forEach(id => delete this.cache[id]);
    },

    sameAttribute(a, b) {
        if (!a || !b) {
            return false;
        }

        return this.getId(a) === this.getId(b);
    },

    supportsFractions(attribute) {
        if (!attribute) {
            return null;
        }

        return this.get(attribute).increments === 0.01;
    }
});